import './utils/rspackPublicPath';
import * as Sentry from '@sentry/browser';
import React from 'react';
import { render } from 'react-dom';
import axios from 'axios';
import Medsender from './app';
import toastr from '../../assets/javascripts/toastr';
import { sanitizeEndpointForSentry } from './global/error_handler';
import { shouldSendSentryEvent } from './utils/sentryFilters';
import { normalizeSentryFrame } from './utils/sentryFrameNormalizer';
import resolveSentryEnvironment from './utils/sentryEnvironment';
import { hasFeatureInLocalStorage } from './global/featureFlags';
import { isAssetRescueActive } from './global/assetRescueState';
import { reportAssetRescueActivation } from './global/reportAssetRescueActivation';
import { scheduleSpaBoot } from './global/spaBootScheduler';
import {
  configureChromeViewerTelemetry,
  createChromeViewerPostHogClient,
  createChromeViewerSentryClient,
} from './utils/chromeViewerTelemetry';

import './assets/stylesheets/main.scss';
import '../../assets/stylesheets/toastr.css';

// Configure axios interceptor to handle NO_FEATURES_RESTRICTED errors.
// When a user with no_features flag tries to access restricted API endpoints,
// redirect them to the referral portal instead of showing a generic error.
//
// IMPORTANT: Only redirects if referral_portal flag is also present.
// If no_features is set without referral_portal, show error instead of redirecting.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const isNoFeaturesRestricted = error.response?.status === 403
      && error.response?.data?.code === 'NO_FEATURES_RESTRICTED';

    if (isNoFeaturesRestricted) {
      const hasReferralPortal = hasFeatureInLocalStorage('referral_portal');

      if (hasReferralPortal) {
        // Show a helpful message and redirect to referral portal
        if (typeof toastr !== 'undefined' && toastr.warning) {
          toastr.warning('Your account has limited access. Redirecting to Referral Portal.');
        }
        if (!window.location.pathname.startsWith('/app/referral_portal')) {
          window.location.href = '/app/referral_portal';
        }
      } else if (typeof toastr !== 'undefined' && toastr.error) {
        // No referral portal access - show error without redirect
        toastr.error('Your account does not have access to this feature. Please contact support.');
      }
      // Reject with a CanceledError so downstream .catch() handlers and
      // error_handler.js recognise this as an intentional cancellation and
      // silently skip it (no error toast, no Sentry report).
      return Promise.reject(new axios.CanceledError('NO_FEATURES_RESTRICTED'));
    }

    return Promise.reject(error);
  },
);

Sentry.init({
  dsn: process.env.SENTRY_REACT_DSN,
  environment: resolveSentryEnvironment(),
  release: process.env.SENTRY_RELEASE,
  integrations: [
    Sentry.rewriteFramesIntegration({ iteratee: normalizeSentryFrame }),
  ],
  ignoreErrors: [
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
  ],
  beforeSend(event) {
    if (!shouldSendSentryEvent(event)) {
      return null;
    }

    try {
      /* eslint-disable no-param-reassign */
      event.extra = event.extra || {};
      event.extra.navigator_onLine = navigator?.onLine;
      event.extra.location_pathname = typeof window !== 'undefined'
        ? sanitizeEndpointForSentry(window.location.pathname)
        : undefined;
      /* eslint-enable no-param-reassign */
    } catch {
      // Never block Sentry reporting
    }

    return event;
  },
});

const chromeViewerTelemetryEnabled = process.env.NODE_ENV !== 'development';
configureChromeViewerTelemetry({
  posthogClient: chromeViewerTelemetryEnabled
    ? createChromeViewerPostHogClient({
      apiKey: process.env.PUBLIC_POSTHOG_KEY,
      apiHost: process.env.PUBLIC_POSTHOG_HOST,
    })
    : null,
  sentryClient: chromeViewerTelemetryEnabled
    ? createChromeViewerSentryClient({
      dsn: process.env.SENTRY_REACT_DSN,
      environment: resolveSentryEnvironment(),
      release: process.env.SENTRY_RELEASE,
    })
    : null,
});

global.toastr = toastr;

// Resolved once: this module executing with the rescue flag set means the
// watchdog injected GCS copies and one is running now. Report it before boot so
// a successful rescue is counted even if the render later throws — without this
// event the rescue is invisible (the post-login probe usually succeeds). The
// call is self-guarding and best-effort; see reportAssetRescueActivation.
const rescued = isAssetRescueActive();
reportAssetRescueActivation();

// Rescue path (see assetRescueWatchdog.inline.js, the canonical design
// reference): scheduleSpaBoot gates the render on document.readyState instead
// of DOMContentLoaded, which the stalled CDN defer scripts delay for minutes.
// #root is resolved inside the boot callback because on the rescue path this
// module can execute before the parser reaches <body>.
scheduleSpaBoot(
  () => render(<Medsender />, document.getElementById('root')),
  { rescued },
);
